import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Smile,
  Meh,
  Frown,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Download,
  Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOOD_OPTIONS = [
  { level: 5, label: "Great", emoji: "😊", color: "bg-green-500", textColor: "text-green-700", bgLight: "bg-green-50" },
  { level: 4, label: "Good", emoji: "🙂", color: "bg-lime-500", textColor: "text-lime-700", bgLight: "bg-lime-50" },
  { level: 3, label: "Okay", emoji: "😐", color: "bg-yellow-500", textColor: "text-yellow-700", bgLight: "bg-yellow-50" },
  { level: 2, label: "Struggling", emoji: "😔", color: "bg-orange-500", textColor: "text-orange-700", bgLight: "bg-orange-50" },
  { level: 1, label: "Crisis", emoji: "😢", color: "bg-red-500", textColor: "text-red-700", bgLight: "bg-red-50" }
];

const MoodTracking = ({ user }) => {
  const navigate = useNavigate();
  const [todayEntry, setTodayEntry] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState("week"); // week or month

  useEffect(() => {
    loadData();
  }, [viewMode]);

  const loadData = async () => {
    try {
      const [todayRes, historyRes] = await Promise.all([
        axios.get(`${API}/veteran/mood/today`),
        axios.get(`${API}/veteran/mood/history?days=${viewMode === "week" ? 7 : 30}`)
      ]);
      
      setTodayEntry(todayRes.data.entry);
      if (todayRes.data.entry) {
        setSelectedMood(todayRes.data.entry.mood_level);
        setNotes(todayRes.data.entry.notes || "");
      }
      
      setHistory(historyRes.data.entries || []);
      setStats(historyRes.data.stats);
    } catch (error) {
      console.error("Failed to load mood data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitMood = async () => {
    if (!selectedMood) {
      toast.error("Please select a mood");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/veteran/mood`, {
        mood_level: selectedMood,
        notes: notes || null
      });
      
      toast.success(todayEntry ? "Mood updated" : "Mood logged for today");
      loadData();
    } catch (error) {
      toast.error("Failed to log mood");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/veteran/mood/export`);
      const data = response.data;
      
      // Create downloadable JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mood-tracking-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Mood data exported successfully");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const getMoodOption = (level) => MOOD_OPTIONS.find(m => m.level === level);

  const getTrendIcon = () => {
    if (!stats?.trend) return null;
    switch (stats.trend) {
      case "improving": return <TrendingUp className="w-4 h-4 text-green-600" />;
      case "declining": return <TrendingDown className="w-4 h-4 text-red-600" />;
      case "stable": return <Minus className="w-4 h-4 text-blue-600" />;
      default: return null;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading mood tracker...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Mood Tracker
                </h1>
                <p className="text-sm text-muted-foreground">
                  Track how you're feeling
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-mood-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Today's Check-in */}
        <Card className="card-valor mb-6">
          <CardHeader>
            <CardTitle>How are you feeling today?</CardTitle>
            <CardDescription>
              {todayEntry ? "You can update your mood anytime" : "Take a moment to check in with yourself"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-2 mb-4">
              {MOOD_OPTIONS.map((mood) => (
                <button
                  key={mood.level}
                  onClick={() => setSelectedMood(mood.level)}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    selectedMood === mood.level
                      ? `${mood.bgLight} border-current ${mood.textColor}`
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid={`mood-${mood.level}`}
                >
                  <span className="text-2xl block mb-1">{mood.emoji}</span>
                  <span className="text-xs font-medium">{mood.label}</span>
                </button>
              ))}
            </div>

            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about how you're feeling (optional)..."
              rows={3}
              className="mb-4"
              data-testid="mood-notes-input"
            />

            <Button 
              onClick={handleSubmitMood} 
              disabled={!selectedMood || isSubmitting}
              className="w-full"
              data-testid="submit-mood-btn"
            >
              {isSubmitting ? "Saving..." : todayEntry ? "Update Today's Mood" : "Log Today's Mood"}
              {todayEntry && <Check className="w-4 h-4 ml-2" />}
            </Button>

            {selectedMood === 1 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium mb-2">
                  We're sorry you're struggling. You're not alone.
                </p>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => navigate("/crisis-resources")}
                >
                  Get Crisis Support
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Overview */}
        {stats && stats.total_entries > 0 && (
          <Card className="card-valor mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Your Mood Overview</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === "week" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("week")}
                  >
                    7 Days
                  </Button>
                  <Button
                    variant={viewMode === "month" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("month")}
                  >
                    30 Days
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{stats.average_mood}</p>
                  <p className="text-xs text-muted-foreground">Avg Mood</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{stats.total_entries}</p>
                  <p className="text-xs text-muted-foreground">Days Tracked</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-center gap-1">
                    {getTrendIcon()}
                    <span className="text-sm font-medium capitalize">
                      {stats.trend === "insufficient_data" ? "Need more data" : stats.trend}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">Trend</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Mood History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <div className="space-y-2">
                {history.slice().reverse().map((entry) => {
                  const mood = getMoodOption(entry.mood_level);
                  return (
                    <div 
                      key={entry.date} 
                      className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
                      data-testid={`history-${entry.date}`}
                    >
                      <div className={`w-10 h-10 rounded-full ${mood?.color} flex items-center justify-center text-white`}>
                        <span className="text-lg">{mood?.emoji}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{formatDate(entry.date)}</span>
                          <Badge variant="outline" className={mood?.textColor}>
                            {mood?.label}
                          </Badge>
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                            {entry.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Meh className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No mood entries yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Start tracking today to see your history
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Privacy Note */}
        <p className="text-sm text-muted-foreground text-center mt-6">
          🔒 Your mood data is private. Only you can see it. 
          Use the Export button to share with your healthcare provider.
        </p>
      </main>
    </div>
  );
};

export default MoodTracking;
